








import {
  Vue,
  Component,
  Prop,
} from "@/shared/vuepack";
import _ from "lodash";
import moment from "moment";
import { GenericGraphPoint } from "@/shared/types";
import { formatCurrency } from "@/shared/currency";
import { GChart } from "vue-google-charts";
import { useThemeStore } from "@/shared/store/theme";

@Component({
  components: {
    GChart
  }
})
export default class GenericLineChart extends Vue {
  themeStore = useThemeStore();
  manualXAxisLabelCutoff = 7;

  @Prop()
  height: number;

  @Prop({ required: false })
  vAxisTitle: string;

  @Prop({ required: false })
  hAxisTitle: string;

  @Prop({ required: true })
  valueLabel: string;

  @Prop({ required: true })
  dateFormatString: string;

  @Prop({ required: true })
  dataSet: GenericGraphPoint[];

  get getChartOptions (): any {
    return {
      fontName: this.themeStore.fontFace,
      annotations: {
        color: this.themeStore.tertiaryColor
      },
      titlePosition: "none",
      colors: [ "#B2BCBB" ],
      tooltip: {
        isHtml: true,
      },
      chartArea: {
        top: 15,
        height: "80%"
      },
      hAxis: {
        title: this.hAxisTitle,
        titleTextStyle: {
          bold: true,
          italic: false,
        },
        baselineColor: "black",
        format: this.dateFormatString,
        gridlines: {
          color: "transparent",
          minSpacing: 90
        },
        ticks: this.xAxisLabels
      },
      vAxis: {
        title: this.vAxisTitle,
        titleTextStyle: {
          bold: true,
          italic: false,
        },
        minValue: 0,
        baselineColor: "black",
        format: "currency",
        gridlines: {
          color: "transparent",
        },
        minorGridlines: {
          color: "transparent",
        }
      },
      pointSize: 0,
      height: this.height,
      legend: { position: "none" },
      enableInteractivity: true,
    };
  }

  tooltipFormatter (data: GenericGraphPoint): string {

    const mainMarkup = `<div style="font-weight: bold; margin-bottom: 3px;">
                          ${moment(data.date, "DD-MMM-YY").format("MMM DD YY")}
                        </div>
                        <div margin-bottom: 3px;">
                          ${this.valueLabel}:
                        </div>
                        <div style="color: ${this.themeStore.tertiaryColor}; font-weight: bold;">
                          ${formatCurrency(data.value)}
                        </div>`;

    const tooltipHtml = `<div style="padding: 2px 10px; min-width: 120px;">
                          ${mainMarkup}
                        </div>`;

    return tooltipHtml;
  }

  formatChartDataItem (chartPoint: GenericGraphPoint): any {
    return [
      moment(chartPoint.date, "DD-MMM-YY").toDate(),
      chartPoint.value,
      `stroke-color: ${this.themeStore.tertiaryColor}; fill-opacity: 0`,
      this.tooltipFormatter(chartPoint),
    ];
  }

  // If we have too few dates, google charts will try to interpolate the date value,
  // resulting in duplcated dates on the X axis. If there are too few, we need to set
  // the x-axis labels manually. Otherwise, let google do its thing.
  get xAxisLabels () {
    const distinctDates = _.uniq(this.dataSet.map(x => x.date));

    return distinctDates.length >= this.manualXAxisLabelCutoff ?
        null : distinctDates.map(x => moment(x, "DD-MMM-YY").toDate());
  }

  get formattedChartData () {
    const formattedChartPoints = this.dataSet.map(this.formatChartDataItem);

    return [
      [
        this.hAxisTitle,
        this.vAxisTitle,
        { type: "string", role: "style" },
        {
          type: "string", role: "tooltip", p: {
            html: true
          }
        },
      ],
      ...formattedChartPoints
    ];
  }
}
